import type { NextPage } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { useAuth } from '@/lib/authContext';
import GuestGuard from '@/components/guards/GuestGuard';

const features = [
  { icon: 'sync', label: 'Sincronización automática con Cegid' },
  { icon: 'balance', label: 'Control fiscal y tributario en tiempo real' },
  { icon: 'smart_toy', label: 'Revisión IA de documentos contables' },
  { icon: 'notifications_active', label: 'Alertas proactivas ante incidencias' },
];

const Home: NextPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Credenciales incorrectas. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GuestGuard>
      <Head>
        <title>Externaliza2 — Asesoría de Empresas</title>
        <meta name="description" content="Plataforma de asesoría empresarial con IA" />
        <link rel="icon" href="/logo.png" />
      </Head>

      <div className="min-h-screen flex">
        {/* ── Left panel — dark branding ── */}
        <div className="hidden lg:flex lg:w-[460px] xl:w-[500px] bg-sidebar relative flex-col justify-between p-10 overflow-hidden flex-shrink-0">
          {/* Decorative blobs */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-fresh-sky-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-steel-azure-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />
          <div className="absolute inset-0 noise-overlay" />

          {/* Logo */}
          <div className="relative z-10 flex items-center gap-3.5">
            <Image src="/logo.png" alt="E2" width={44} height={44} className="w-11 h-11 drop-shadow-lg" />
            <div>
              <span className="text-xl font-bold text-white font-display tracking-tight block">EXTERNALIZA2</span>
              <span className="text-[11px] text-sidebar-muted font-medium tracking-wide uppercase">Asesoría de Empresas</span>
            </div>
          </div>

          {/* Center content */}
          <div className="relative z-10 space-y-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-fresh-sky-400 mb-4">
                La plataforma todo-en-uno
              </p>
              <blockquote className="text-2xl font-display font-semibold text-white/90 leading-relaxed">
                "Gestión contable, fiscal y documental centralizada para tu asesoría."
              </blockquote>
            </div>

            {/* Feature list */}
            <ul className="space-y-3.5">
              {features.map((f) => (
                <li key={f.icon} className="flex items-center gap-3 text-sm text-white/70">
                  <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-fresh-sky-500/20 flex items-center justify-center">
                    <span className="material-symbols-outlined text-base text-fresh-sky-300">{f.icon}</span>
                  </span>
                  {f.label}
                </li>
              ))}
            </ul>

            {/* Trust badge */}
            <div className="flex items-center gap-2 pt-2">
              <span className="material-symbols-outlined text-sm text-fresh-sky-400">verified_user</span>
              <p className="text-xs text-sidebar-muted">GDPR Compliant · Datos alojados en la UE</p>
            </div>
          </div>

          <p className="relative z-10 text-xs text-sidebar-muted/50">
            © {new Date().getFullYear()} Externaliza2 Consultores S.L.
          </p>
        </div>

        {/* ── Right panel — login form ── */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-surface min-h-screen">
          <div className="w-full max-w-[400px]">

            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-3 mb-10">
              <Image src="/logo.png" alt="E2" width={40} height={40} className="w-10 h-10" />
              <div>
                <span className="text-base font-bold font-display text-calm-text tracking-tight block">EXTERNALIZA2</span>
                <span className="text-[10px] text-calm-muted uppercase tracking-widest">Asesoría de Empresas</span>
              </div>
            </div>

            {/* Header */}
            <div className="mb-8 animate-fade-in">
              <h1 className="text-3xl font-bold font-display text-calm-text tracking-tight">
                Bienvenido de nuevo
              </h1>
              <p className="text-sm text-calm-muted mt-2">
                Accede con tus credenciales para continuar
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5 animate-slide-up">

              {/* Email */}
              <div>
                <label htmlFor="email" className="label">Correo electrónico</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-[18px] text-calm-muted pointer-events-none select-none">
                    mail
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="tu@empresa.com"
                    autoComplete="email"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="label !mb-0">Contraseña</label>
                  <Link
                    href="/forgot-password"
                    className="text-xs font-medium text-fresh-sky-600 hover:text-fresh-sky-700 transition-colors"
                  >
                    ¿Olvidaste la contraseña?
                  </Link>
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-[18px] text-calm-muted pointer-events-none select-none">
                    lock
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-11"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-calm-muted hover:text-calm-text transition-colors p-0.5"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm animate-fade-in">
                  <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5 select-none">error</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="w-full btn btn-primary py-3 text-sm font-medium mt-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                    Verificando acceso…
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-base">login</span>
                    Iniciar Sesión
                  </>
                )}
              </button>
            </form>

            {/* Footer trust line */}
            <p className="mt-8 text-center text-xs text-calm-muted/60 flex items-center justify-center gap-1.5">
              <span className="material-symbols-outlined text-[13px] select-none">shield</span>
              Conexión segura · Datos cifrados
            </p>
          </div>
        </div>
      </div>
    </GuestGuard>
  );
};

export default Home;
